import { widgetOrigin } from "../shared/origin";

/**
 * Where the cart widget keeps its cart (T-159).
 *
 * TWO BACKENDS, chosen by origin:
 *
 *   SAME-ORIGIN (the app itself, or a Cobuntu-proxied apex) — `localStorage`,
 *   byte-for-byte what this widget has always done. The community app's React
 *   provider writes the same key, so the two stay one cart. Nothing about
 *   existing embeds changes, which is the point: the proxied customers who
 *   work today carry no risk from this.
 *
 *   CROSS-ORIGIN (a decoupled landing like dddimo.com, where the app lives on
 *   app.dddimo.com) — the server-side cart API. localStorage is partitioned by
 *   origin, so the landing's store is a DIFFERENT store from the app's: the
 *   widget rendered a cart nothing could ever put anything into. The server
 *   cart is addressed by an opaque token this module holds.
 *
 * ── The sync/async seam ─────────────────────────────────────────────────────
 *
 * The widget is written against a synchronous store (`readCart()` returns an
 * array, now). A network cart cannot be synchronous, so this module keeps a
 * module-level CACHE that `snapshot()` returns immediately, and hydrates it in
 * the background. Every mutation updates the server and then the cache, and
 * notifies, so the badge and drawer re-render from the same path they always
 * did. The alternative — making four call sites async — would have spread
 * awaits through render code that has no way to show a pending state.
 */

export interface CartItem {
    id: string;
    type: "EVENT" | "PRODUCT";
    sku: string;
    productId: string;
    name: string;
    price: number;
    currency: string;
    quantity?: number;
    imageUrl?: string;
    eventId?: string;
    eventDate?: string;
    eventLocation?: string;
    communityListingId?: string;
    addedAt: string;
    /** Server carts only: set when the line can no longer be bought. */
    unavailableReason?: string | null;
    /** Server carts only: the chosen variant. */
    tierId?: string | null;
    tierName?: string | null;
}

const STORAGE_KEY = "cobuntu_community_cart";
const TOKEN_KEY = "cobuntu_cart_token";

/**
 * True when the page embedding this widget is NOT the app's own origin.
 *
 * `widgetOrigin()` is where the bundle was served from, i.e. the app host. On
 * a proxied apex that equals `location.origin`, so this is false and nothing
 * changes for those embeds.
 */
export function isCrossOrigin(): boolean {
    try {
        return !!widgetOrigin() && widgetOrigin() !== location.origin;
    } catch {
        return false;
    }
}

// ─── Local backend ──────────────────────────────────────────────────────────

function readLocal(): CartItem[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function writeLocal(items: CartItem[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
        /* quota — the caller surfaces it */
    }
}

// ─── Server backend ─────────────────────────────────────────────────────────

let cache: CartItem[] = [];
let hydrated = false;
let hydrating: Promise<void> | null = null;

function getToken(): string | null {
    try {
        return localStorage.getItem(TOKEN_KEY);
    } catch {
        return null;
    }
}

function setToken(t: string): void {
    try {
        localStorage.setItem(TOKEN_KEY, t);
    } catch {
        /* private mode — the cart lives for this page only */
    }
}

/** Server line → the widget's item shape, so render code is unchanged. */
function toItem(l: any): CartItem {
    return {
        id: String(l.id),
        type: (l.productType === "EVENT" ? "EVENT" : "PRODUCT") as "EVENT" | "PRODUCT",
        sku: l.sku ?? "",
        productId: String(l.productId),
        // The variant name belongs beside the product name, the way the app
        // shows it — otherwise "Small" and "Large" render as the same line.
        name: l.tierName ? `${l.name} — ${l.tierName}` : String(l.name ?? ""),
        price: typeof l.unitPrice === "number" ? l.unitPrice : 0,
        currency: l.currency ?? "EUR",
        quantity: typeof l.quantity === "number" ? l.quantity : 1,
        imageUrl: l.imageUrl ?? undefined,
        addedAt: new Date().toISOString(),
        unavailableReason: l.unavailableReason ?? null,
        tierId: l.tierId ?? null,
        tierName: l.tierName ?? null,
    };
}

export interface ServerCartDeps {
    apiUrl: string;
    communityTag: string;
    publishableKey: string;
    /** Called after the cache changes, so the badge and drawer re-render. */
    onChange: () => void;
}

let deps: ServerCartDeps | null = null;

/** Wire the server backend. No-op for the same-origin case. */
export function configureServerCart(d: ServerCartDeps): void {
    deps = d;
}

async function api(path: string, init: RequestInit = {}): Promise<Response> {
    if (!deps) throw new Error("server cart not configured");
    const token = getToken();
    return fetch(`${deps.apiUrl}/api/v1/communities/${encodeURIComponent(deps.communityTag)}/cart${path}`, {
        ...init,
        // `omit`, deliberately. The auth cookie is host-scoped and would not
        // be sent cross-origin anyway; asking for it invites a preflight that
        // requires credentials to be allowed and buys nothing.
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            "X-API-Key": deps.publishableKey,
            ...(token ? { "X-Cobuntu-Cart-Token": token } : {}),
            ...(init.headers ?? {}),
        },
    });
}

/** Ensure a cart exists and we hold a token for it. */
async function ensureCart(): Promise<void> {
    if (getToken()) return;
    const res = await api("", { method: "POST" });
    if (!res.ok) throw new Error(`cart create ${res.status}`);
    const body = await res.json();
    if (body?.token) setToken(body.token);
    cache = (body?.cart?.items ?? []).map(toItem);
}

async function hydrate(): Promise<void> {
    if (!deps) return;
    if (!getToken()) {
        // Nothing to load, and minting a cart just to look at it would create
        // a row for every visitor who never adds anything.
        hydrated = true;
        return;
    }
    const res = await api("");
    if (res.status === 404) {
        // The cart was purged, or the token is from another community. Drop it
        // so the next add mints a fresh cart rather than looping on a 404.
        try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
        cache = [];
        hydrated = true;
        return;
    }
    if (!res.ok) return; // leave the cache alone; a transient failure is not an empty cart
    const body = await res.json();
    cache = (body?.cart?.items ?? []).map(toItem);
    hydrated = true;
}

/** Load the server cart once, in the background. Safe to call repeatedly. */
export function primeServerCart(): Promise<void> {
    if (!isCrossOrigin() || !deps) return Promise.resolve();
    if (hydrated) return Promise.resolve();
    if (!hydrating) {
        hydrating = hydrate()
            .catch(() => { /* offline: keep whatever the cache has */ })
            .finally(() => {
                hydrating = null;
                deps?.onChange();
            });
    }
    return hydrating;
}

// ─── Public API ─────────────────────────────────────────────────────────────

/** The cart, right now, synchronously. */
export function snapshot(): CartItem[] {
    return isCrossOrigin() ? cache : readLocal();
}

/** Add a product (server backend only; the app writes its own cart directly). */
export async function addItem(input: {
    productId: string;
    tierId?: string | null;
    quantity?: number;
}): Promise<void> {
    if (!isCrossOrigin()) throw new Error("addItem is for the cross-origin cart");
    await ensureCart();
    const res = await api("/items", { method: "POST", body: JSON.stringify(input) });
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `add failed (${res.status})`);
    }
    const body = await res.json();
    cache = (body?.cart?.items ?? []).map(toItem);
    deps?.onChange();
}

export async function removeItem(itemId: string): Promise<void> {
    if (!isCrossOrigin()) {
        writeLocal(readLocal().filter((i) => i.id !== itemId));
        return;
    }
    const res = await api(`/items/${encodeURIComponent(itemId)}`, { method: "DELETE" });
    if (res.ok) {
        const body = await res.json();
        cache = (body?.cart?.items ?? []).map(toItem);
    }
    deps?.onChange();
}

export async function setQuantity(itemId: string, quantity: number): Promise<void> {
    if (!isCrossOrigin()) {
        const items = readLocal().map((i) => (i.id === itemId ? { ...i, quantity } : i));
        writeLocal(quantity <= 0 ? items.filter((i) => i.id !== itemId) : items);
        return;
    }
    const res = await api(`/items/${encodeURIComponent(itemId)}`, {
        method: "PATCH",
        body: JSON.stringify({ quantity }),
    });
    if (res.ok) {
        const body = await res.json();
        cache = (body?.cart?.items ?? []).map(toItem);
    }
    deps?.onChange();
}

/**
 * Mint the one-time code that carries this cart to the app, and return the
 * checkout URL to navigate to.
 *
 * The CODE goes in the URL, never the token: the token is long-lived and a URL
 * leaks into history, Referer and logs.
 */
export async function handoffUrl(appOrigin: string): Promise<string> {
    const res = await api("/handoff", { method: "POST" });
    if (!res.ok) throw new Error(`handoff failed (${res.status})`);
    const body = await res.json();
    return `${appOrigin}/checkout?c=${encodeURIComponent(body.code)}`;
}

/** Local-backend passthrough, kept so same-origin code paths are unchanged. */
export function writeLocalCart(items: CartItem[]): void {
    writeLocal(items);
}
